import { Link, useParams } from 'react-router-dom';
import { useEffect, useState, useContext } from 'react';
import Loader from '../shared/Loader';
import { useDispatch, useSelector } from 'react-redux';
import { addToCart } from '../../store/cartSlice';
import { ProductContext } from '../../providers/ProductContext';
import { QuantityPricingContext } from '../../providers/QuantityPricingContext';
import { getPricingScopeKey } from '../../utils/quantityPricing';
import { FormattedDescription, HtmlFormattedDescription } from '../../utils/formatDescription';
import { showError, showCartToast } from '../../utils/sweetAlert';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import TryOnModal from './TryOnModal';
import CustomOrderForm from './CustomOrderForm';
import SizeGuideModal from './SizeGuideModal';
import SEO from '../shared/SEO';
import { isCustomizableProduct } from '../../utils/isCustomizableProduct';


const SingleProduct = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { imageCache, products, loading: productsLoading } = useContext(ProductContext);
  const { getTiersForCategory, getUnitPrice, breakpoints: qtyBreakpoints } = useContext(QuantityPricingContext);
  const cartItems = useSelector((state) => state.cart.items);
  
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showTryOnModal, setShowTryOnModal] = useState(false);
  const [showCustomOrderForm, setShowCustomOrderForm] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [selectedAIProductType, setSelectedAIProductType] = useState('');
  const [selectedAISize, setSelectedAISize] = useState('');
  const [selectedAIColor, setSelectedAIColor] = useState('');

  // Tipos de producto disponibles para productos AI con sus precios
  // Los precios se obtienen de las variantes del producto (MongoDB)
  // Índice 0 = Remera, Índice 1 = Cuadro, Índice 2 = Almohadón
  const getAIProductTypes = () => {
    if (!product?.variants || product.variants.length < 3) {
      // Fallback si no hay variantes
      return [
        { id: 'cuadro', name: 'Cuadro', price: 0, icon: 'fa-solid fa-image' },
        { id: 'remera', name: 'Remera', price: 0, icon: 'fa-solid fa-shirt' },
        { id: 'almohadon', name: 'Almohadón', price: 0, icon: 'fa-solid fa-couch' }
      ];
    }
    return [
      { id: 'cuadro', name: 'Cuadro', price: product.variants[1]?.price || 0, icon: 'fa-solid fa-image' },
      { id: 'remera', name: 'Remera', price: product.variants[0]?.price || 0, icon: 'fa-solid fa-shirt' },
      { id: 'almohadon', name: 'Almohadón', price: product.variants[2]?.price || 0, icon: 'fa-solid fa-couch' }
    ];
  };

  // Talles y colores disponibles para remeras AI
  const aiRemeraSizes = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
  const aiRemeraColors = ['Blanco', 'Negro', 'Gris'];

  useEffect(() => {
    // Reutiliza el producto ya cargado en el catálogo global (ProductContext)
    // para evitar una llamada de red redundante en cada visita a la ficha.
    const cachedProduct = products.find(p => p._id === id);
    if (cachedProduct) {
      setProduct(cachedProduct);
      setError(null);
      setLoading(false);
      return;
    }

    // Si el catálogo todavía está cargando, esperamos: puede que el producto
    // aparezca ahí apenas termine, sin necesidad de un fetch individual.
    if (productsLoading) {
      return;
    }

    // Fallback: el producto no está en el catálogo ya cargado (por ejemplo,
    // fue creado después del último fetch global). Se busca individualmente.
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/products/${id}`)
      .then(res => res.json())
      .then(data => {
        setProduct(data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError('Error al cargar el producto');
        setLoading(false);
      });
  }, [id, products, productsLoading]);

  useEffect(() => {
    if (!product) return;
    setSelectedSize('');
    setSelectedVariant(null);
    if (product.hasVariants && product.variants?.length) {
      const colors = [...new Set(product.variants.filter(v => v.isActive !== false).map(v => v.color))];
      setSelectedColor(colors.length === 1 ? colors[0] : '');
    } else {
      setSelectedColor('');
    }
  }, [product?._id]);

  // Obtener variantes disponibles y opciones únicas
  const getAvailableSizes = () => {
    if (!product?.hasVariants || !product.variants?.length) {
      return product?.size || [];
    }
    return [...new Set(product.variants.filter(v => v.isActive !== false).map(v => v.size))];
  };

  const getAvailableColors = () => {
    if (!product?.hasVariants || !product.variants?.length) {
      return product?.color || [];
    }
    return [...new Set(product.variants.filter(v => v.isActive !== false).map(v => v.color))];
  };

  const hasMultipleColors = () => getAvailableColors().length > 1;

  const getImplicitColor = () => {
    const colors = getAvailableColors();
    return colors.length === 1 ? colors[0] : null;
  };

  // Obtener variantes compatibles con la selección actual
  const getCompatibleVariants = () => {
    if (!product?.hasVariants || !product.variants?.length) return [];
    
    return product.variants.filter(variant => {
      if (variant.isActive === false) return false;
      if (selectedSize && variant.size !== selectedSize) return false;
      if (selectedColor && variant.color !== selectedColor) return false;
      return true;
    });
  };

  // Maneja la selección de talla
  const handleSizeChange = (size) => {
    setSelectedSize(size);
    const color = hasMultipleColors() ? selectedColor : getImplicitColor();
    updateSelectedVariant(size, color);
  };

  // Maneja la selección de color
  const handleColorChange = (color) => {
    setSelectedColor(color);
    updateSelectedVariant(selectedSize, color);
  };

  // Actualiza la variante seleccionada
  const updateSelectedVariant = (size, color) => {
    if (!product?.hasVariants || !size) {
      setSelectedVariant(null);
      return;
    }

    const effectiveColor = color || getImplicitColor();
    if (!effectiveColor) {
      setSelectedVariant(null);
      return;
    }

    const variant = product.variants.find(v => 
      v.size === size && v.color === effectiveColor && v.isActive !== false
    );
    setSelectedVariant(variant || null);
  };

  // Maneja la selección múltiple para talles (legacy)
  const handleSizeSelectChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setSelectedSizes(selectedOptions);
  };

  // Maneja la selección múltiple para colores (legacy)
  const handleColorSelectChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setSelectedColors(selectedOptions);
  };

  const handleAddToCart = () => {
    // Validación para productos AI
    if (isAIProduct()) {
      if (!selectedAIProductType) {
        showError('Por favor, selecciona el tipo de producto (Cuadro, Remera o Almohadón)');
        return;
      }
      
      // Validación adicional para remeras AI
      if (selectedAIProductType === 'remera') {
        if (!selectedAISize) {
          showError('Por favor, selecciona una talla para la remera');
          return;
        }
        if (!selectedAIColor) {
          showError('Por favor, selecciona un color para la remera');
          return;
        }
      }
      
      const selectedType = getAIProductTypes().find(t => t.id === selectedAIProductType);
      const finalPrice = product.discount > 0 
        ? selectedType.price - (selectedType.price * product.discount / 100)
        : selectedType.price;

      // Construir info de variante según el tipo
      const variantInfo = selectedAIProductType === 'remera' 
        ? `${selectedType.name} - ${selectedAISize} - ${selectedAIColor}`
        : selectedType.name;

      dispatch(addToCart({
        ...product,
        price: finalPrice,
        quantity,
        selectedVariant: {
          size: selectedAIProductType === 'remera' ? selectedAISize : selectedType.name,
          color: selectedAIProductType === 'remera' ? selectedAIColor : 'Único',
          productType: selectedAIProductType
        },
        variantInfo,
        isAIProduct: true
      }));
      
      showCartToast(`${product.name} - ${variantInfo}`);
      return;
    }

    if (product.hasVariants && product.variants?.length > 0) {
      // Validación para productos con variantes
      if (!selectedSize) {
        showError('Por favor, selecciona una talla');
        return;
      }
      if (hasMultipleColors() && !selectedColor) {
        showError('Por favor, selecciona un color');
        return;
      }
      if (!selectedVariant) {
        showError('La combinación seleccionada no está disponible');
        return;
      }

      // Calcular precio de la variante
      const variantPrice = selectedVariant.price || product.basePrice || product.price;
      const discountedVariantPrice = product.discount > 0 
        ? variantPrice - (variantPrice * product.discount / 100)
        : variantPrice;
      const finalPrice = getEffectiveUnitPrice(quantity, discountedVariantPrice);

      dispatch(addToCart({
        ...product,
        price: finalPrice,
        unitBasePrice: discountedVariantPrice,
        quantity,
        selectedVariant: {
          size: selectedVariant.size,
          color: selectedVariant.color,
          sku: selectedVariant.sku,
          variantId: selectedVariant._id,
          images: selectedVariant.images || []
        },
        variantInfo: hasMultipleColors()
          ? `${selectedVariant.size} - ${selectedVariant.color}`
          : selectedVariant.size
      }));
      
      showCartToast(product.name);
    } else {
      // Validaciones legacy para productos sin variantes
      if (product.size?.length && selectedSizes.length === 0) {
        showError('Por favor, selecciona al menos un talle');
        return;
      }
      if (product.color?.length && selectedColors.length === 0) {
        showError('Por favor, selecciona al menos un color');
        return;
      }
      
      // Calcular el precio final para productos legacy
      const basePrice = product.basePrice || product.price;
      const discountedBasePrice = product.discount > 0 
        ? basePrice - (basePrice * product.discount / 100)
        : basePrice;
      const finalPrice = getEffectiveUnitPrice(quantity, discountedBasePrice);
    
      dispatch(addToCart({
        ...product,
        price: finalPrice,
        unitBasePrice: discountedBasePrice,
        quantity,
        selectedSizes,
        selectedColors
      }));
      
      showCartToast(product.name);
    }
  };
  

  if (loading) {
    return <Loader />;
  }
  
  if (error) {
    return (
      <div className="single-product-error">
        <div className="container">
          <div className="error-content">
            <i className="fa-solid fa-exclamation-triangle"></i>
            <h3>Error al cargar el producto</h3>
            <p>{error}</p>
            <button 
              className="btn-retry"
              onClick={() => window.location.reload()}
            >
              <i className="fa-solid fa-refresh me-2"></i>
              Intentar de nuevo
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (!product) {
    return (
      <div className="single-product-not-found">
        <div className="container">
          <div className="not-found-content">
            <i className="fa-solid fa-search"></i>
            <h3>Producto no encontrado</h3>
            <p>El producto que buscas no existe o ha sido eliminado</p>
            <button 
              className="btn-back-store"
              onClick={() => window.history.back()}
            >
              <i className="fa-solid fa-arrow-left me-2"></i>
              Volver a la tienda
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Obtener URL de imagen correcta (directo desde Wasabi, sin proxy)
  const getImageUrl = (imageUrl) => getDisplayImageUrl(imageUrl);

  const getCategoryExtraImages = () => {
    const category = typeof product.category === 'object' ? product.category : null;
    if (!category) return [];

    const resolveImages = (source) => {
      if (!source?.productExtraImagesEnabled || !Array.isArray(source.productExtraImages)) {
        return [];
      }
      return source.productExtraImages
        .filter(image => image?.url && image.isActive !== false)
        .map(image => getImageUrl(image.url));
    };

    const subcategoryName = product.subcategory;
    if (subcategoryName && Array.isArray(category.subcategories)) {
      const subcategory = category.subcategories.find(sub =>
        sub?.name === subcategoryName ||
        subcategoryName.includes(sub?.name) ||
        sub?.name?.includes(subcategoryName)
      );
      const subcategoryImages = resolveImages(subcategory);
      if (subcategoryImages.length > 0) {
        return subcategoryImages;
      }
    }

    return resolveImages(category);
  };

  // Obtener todas las imágenes disponibles
  const getAllImages = () => {
    let baseImages = [];

    // Priorizar imágenes del producto principal
    if (product.images?.length > 0) {
      baseImages = product.images.map(img => getImageUrl(img));
    } else if (product.image) {
      // Si no hay array de imágenes, usar la imagen principal
      const imageUrl = imageCache[product.image] || getImageUrl(product.image);
      baseImages = [imageUrl];
    } else {
      // Fallback a imagen por defecto
      baseImages = ['https://via.placeholder.com/400x400?text=Sin+Imagen'];
    }

    const categoryExtraImages = getCategoryExtraImages();
    const seenImages = new Set(baseImages);
    const uniqueExtraImages = categoryExtraImages.filter(image => {
      if (seenImages.has(image)) return false;
      seenImages.add(image);
      return true;
    });

    return [...baseImages, ...uniqueExtraImages];
  };

  const allImages = getAllImages();
  const currentImage = allImages[currentImageIndex] || allImages[0];

  // Funciones para navegación de imágenes
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  // Check if product is AI category
  const isAIProduct = () => {
    if (!product) return false;
    const categoryName = typeof product.category === 'object' 
      ? product.category?.name 
      : product.category;
    return categoryName === 'AI';
  };

  // Calcular precio actual (variante o base)
  const getCurrentPrice = () => {
    // Para productos AI, usar el precio del tipo seleccionado
    if (isAIProduct() && selectedAIProductType) {
      const selectedType = getAIProductTypes().find(t => t.id === selectedAIProductType);
      return selectedType?.price || product.basePrice || product.price;
    }
    
    if (selectedVariant) {
      return selectedVariant.price || product.basePrice || product.price;
    }
    return product.basePrice || product.price;
  };

  const currentPrice = getCurrentPrice();
  
  // Calcula el precio con descuento
  const discountedPrice = product.discount > 0 
    ? (currentPrice - currentPrice * product.discount / 100).toFixed(2)
    : currentPrice;

  const ahorro = (currentPrice - discountedPrice).toFixed(2);

  // Precios por cantidad (descuento por volumen), configurados por categoría.
  // La franja se calcula sobre la cantidad TOTAL de la categoría en el carrito
  // (otros productos de la misma categoría ya agregados) + la cantidad que se
  // está seleccionando ahora, para que el descuento aplique aunque el cliente
  // combine productos distintos de la misma categoría (ej. Canguros diferentes).
  const categoryId = product.category && typeof product.category === 'object'
    ? product.category._id
    : product.category;
  const qtyTiers = getTiersForCategory(categoryId, product.subcategory);
  const hasQtyTiers = qtyTiers.length > 0;
  const pricingKey = getPricingScopeKey(categoryId, product.subcategory);
  const existingCategoryQtyInCart = cartItems.reduce((acc, item) => (
    getPricingScopeKey(item.category, item.subcategory) === pricingKey ? acc + item.quantity : acc
  ), 0);
  const getEffectiveUnitPrice = (qty, basePriceForCalc) => (
    hasQtyTiers ? getUnitPrice(categoryId, product.subcategory, existingCategoryQtyInCart + qty, basePriceForCalc) : Number(basePriceForCalc)
  );
  const displayUnitPrice = getEffectiveUnitPrice(quantity, discountedPrice);

  // Check if product is suitable for virtual try-on (clothing items)
  const isTryOnEligible = () => {
    if (!product) return false;
    
    const categoryName = typeof product.category === 'object' 
      ? product.category?.name?.toLowerCase() 
      : product.category?.toLowerCase();
    
    const productName = product.name?.toLowerCase() || '';
    const subcategory = product.subcategory?.toLowerCase() || '';
    
    // Check for clothing categories and keywords
    const clothingKeywords = [
      'remera', 'camiseta', 'playera', 'polo', 'camisa', 'blusa',
      'sweater', 'hoodie', 'sudadera', 'jersey', 'cardigan',
      'vestido', 'falda', 'pantalon', 'jean', 'short',
      'chaqueta', 'campera', 'abrigo', 'saco'
    ];
    
    const clothingCategories = [
      'ropa', 'clothing', 'apparel', 'fashion', 'moda',
      'remeras', 'camisetas', 'tops', 'bottoms'
    ];
    
    return clothingCategories.some(cat => categoryName?.includes(cat)) ||
           clothingKeywords.some(keyword => 
             productName.includes(keyword) || 
             subcategory.includes(keyword)
           );
  };

  const isCustomizable = () => isCustomizableProduct(product);

  // Get product type for size guide (remera, buzo, canguro)
  const getSizeGuideType = () => {
    if (!product) return null;
    
    const productName = product.name?.toLowerCase() || '';
    const categoryName = typeof product.category === 'object' 
      ? product.category?.name?.toLowerCase() 
      : product.category?.toLowerCase() || '';
    const subcategory = product.subcategory?.toLowerCase() || '';
    
    const searchText = `${productName} ${categoryName} ${subcategory}`;
    
    // Detectar remeras
    if (searchText.includes('remera') || searchText.includes('camiseta') || 
        searchText.includes('t-shirt') || searchText.includes('tshirt') ||
        searchText.includes('polera')) {
      return 'remera';
    }
    
    // Detectar buzos (para futuro)
    if (searchText.includes('buzo') || searchText.includes('sudadera') || 
        searchText.includes('sweater') || searchText.includes('suéter')) {
      return 'buzo';
    }
    
    // Detectar canguros (para futuro)
    if (searchText.includes('canguro') || searchText.includes('hoodie') || 
        searchText.includes('capucha')) {
      return 'canguro';
    }
    
    return null;
  };

  // Preparar descripción para SEO (limpiar formato)
  const getSeoDescription = () => {
    if (!product.description) return `Compra ${product.name} en REM Enterprises. Envío a todo Uruguay.`;
    const cleanDesc = product.description.replace(/\\n/g, ' ').replace(/\n/g, ' ').substring(0, 160);
    return cleanDesc + (product.description.length > 160 ? '...' : '');
  };

  return (
    <>
      <SEO 
        title={product.name}
        description={getSeoDescription()}
        image={currentImage}
        url={`/product/${product._id}`}
        type={isCustomizable() ? 'website' : 'product'}
        product={isCustomizable() ? null : {
          name: product.name,
          description: product.description,
          price: parseFloat(discountedPrice),
          images: allImages,
          sku: product.sku || product._id,
          category: product.category,
          stock: product.stock ?? 10,
          brand: 'REM Enterprises'
        }}
        breadcrumbs={[
          { name: 'Inicio', url: '/' },
          { name: 'Tienda', url: '/tienda' },
          { name: product.category?.name || 'Productos', url: `/tienda?category=${product.category?.name || ''}` },
          { name: product.name, url: `/product/${product._id}` }
        ]}
      />
      <div className="single-product-container">
        {/* Breadcrumb */}
      <div className="container">
        <nav className="product-breadcrumb" aria-label="Breadcrumb">
          <Link to="/" className="breadcrumb-item">
            <i className="fa-solid fa-home me-1"></i>
            Inicio
          </Link>
          <span className="breadcrumb-separator">/</span>
          <Link to="/tienda" className="breadcrumb-item">
            <i className="fa-solid fa-store me-1"></i>
            Tienda
          </Link>
          <span className="breadcrumb-separator">/</span>
          <Link
            to={`/tienda?category=${encodeURIComponent(product.category?.name || product.category || '')}`}
            className="breadcrumb-item"
          >
            <i className="fa-solid fa-tag me-1"></i>
            {product.category?.name || product.category}
          </Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current" aria-current="page">{product.name}</span>
        </nav>
      </div>

      <div className="container">
        <div className="product-layout">
          {/* Product Gallery */}
          <div className="product-gallery">
            <div className="main-image-container">
              <div className="main-image-wrapper">
                <img 
                  src={currentImage} 
                  alt={product.name} 
                  className="main-product-image"
                />
                
                {/* Image Navigation */}
                {allImages.length > 1 && (
                  <>
                    <button 
                      className="image-nav-btn prev-btn"
                      onClick={prevImage}
                    >
                      <i className="fa-solid fa-chevron-left"></i>
                    </button>
                    <button 
                      className="image-nav-btn next-btn"
                      onClick={nextImage}
                    >
                      <i className="fa-solid fa-chevron-right"></i>
                    </button>
                    
                    {/* Image Indicators */}
                    <div className="image-indicators">
                      {allImages.map((_, index) => (
                        <button
                          key={index}
                          className={`indicator-dot ${index === currentImageIndex ? 'active' : ''}`}
                          onClick={() => goToImage(index)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Thumbnail Gallery */}
            {allImages.length > 1 && (
              <div className="thumbnail-gallery">
                {allImages.map((image, index) => (
                  <div
                    key={index}
                    className={`thumbnail-item ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => goToImage(index)}
                  >
                    <img
                      src={image}
                      alt={`${product.name} ${index + 1}`}
                      className="thumbnail-image"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="product-details">
            <div className="product-header">
              <h1 className="product-title">{product.name}</h1>
              
              {/* Product Meta */}
              <div className="product-meta">
                <div className="meta-item">
                  <i className="fa-solid fa-tag"></i>
                  <span>{product.category?.name || product.category}</span>
                </div>
                {product.subcategory && (
                  <div className="meta-item">
                    <i className="fa-solid fa-bookmark"></i>
                    <span>{product.subcategory}</span>
                  </div>
                )}
                {product.brand && (
                  <div className="meta-item">
                    <i className="fa-solid fa-award"></i>
                    <span>{product.brand}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Los productos personalizados se cotizan manualmente. */}
            <div className="price-section">
              {isCustomizable() ? (
                <div className="quote-request">
                  <i className="fa-solid fa-comments"></i>
                  <div>
                    <strong>Solicitá tu cotización</strong>
                    <span>El precio varía según el tipo de producto, la cantidad y los estampados.</span>
                  </div>
                </div>
              ) : product.discount > 0 ? (
                <div className="price-with-discount">
                  <div className="discount-badge">
                    <i className="fa-solid fa-percent me-1"></i>
                    {product.discount}% OFF
                  </div>
                  <div className="price-breakdown">
                    <span className="original-price">${currentPrice}</span>
                    <span className="final-price">${discountedPrice}</span>
                  </div>
                  <div className="savings">
                    <i className="fa-solid fa-piggy-bank me-1"></i>
                    Ahorras ${ahorro}
                  </div>
                </div>
              ) : (
                <div className="price-simple">
                  <span className="current-price">${displayUnitPrice.toLocaleString('es-AR')}</span>
                </div>
              )}
              {!isCustomizable() && hasQtyTiers && (
                <div className="qty-tiers-legend">
                  <i className="fa-solid fa-tags me-1"></i>
                  {qtyBreakpoints.map((bp, i) => {
                    const tier = qtyTiers.find(t => t.minQuantity === bp);
                    if (!tier) return null;
                    return (
                      <span key={bp} className="qty-tier-item">
                        {i > 0 && ' · '}
                        {bp === 1 ? '1 unidad' : `Desde ${bp}`}: <strong>${Number(tier.price).toLocaleString('es-AR')} c/u</strong>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Product Description */}
            <div className="product-description">
              {product.description?.includes('<') ? (
                <HtmlFormattedDescription 
                  description={product.description} 
                  className="description-content"
                />
              ) : (
                <FormattedDescription 
                  description={product.description} 
                  className="description-content"
                />
              )}
            </div>

            {/* AI Product Type Selection */}
            {isAIProduct() && (
              <div className="ai-product-selection">
                <div className="variant-group">
                  <label className="variant-label">
                    <i className="fa-solid fa-wand-magic-sparkles me-2"></i>
                    Selecciona el tipo de producto
                  </label>
                  <div className="ai-product-types">
                    {getAIProductTypes().map((type) => (
                      <button
                        key={type.id}
                        className={`ai-product-type-btn ${selectedAIProductType === type.id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedAIProductType(type.id);
                          // Resetear talla y color si cambia el tipo
                          if (type.id !== 'remera') {
                            setSelectedAISize('');
                            setSelectedAIColor('');
                          }
                        }}
                      >
                        <i className={`${type.icon} type-icon`}></i>
                        <span className="type-name">{type.name}</span>
                        <span className="type-price">${type.price.toLocaleString('es-AR')}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Selectores de Talla y Color para Remeras AI */}
                {selectedAIProductType === 'remera' && (
                  <>
                    <div className="variant-group">
                      <div className="variant-label-row">
                        <label className="variant-label">
                          <i className="fa-solid fa-ruler me-2"></i>
                          Talla
                        </label>
                        <button 
                          type="button"
                          className="size-guide-link"
                          onClick={() => setShowSizeGuide(true)}
                        >
                          <i className="fa-solid fa-ruler-combined me-1"></i>
                          Ver guía de talles
                        </button>
                      </div>
                      <div className="variant-options">
                        {aiRemeraSizes.map((size) => (
                          <button
                            key={size}
                            className={`variant-btn size-btn ${selectedAISize === size ? 'selected' : ''}`}
                            onClick={() => setSelectedAISize(size)}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="variant-group">
                      <label className="variant-label">
                        <i className="fa-solid fa-palette me-2"></i>
                        Color
                      </label>
                      <div className="variant-options">
                        {aiRemeraColors.map((color) => (
                          <button
                            key={color}
                            className={`variant-btn color-btn ${selectedAIColor === color ? 'selected' : ''}`}
                            onClick={() => setSelectedAIColor(color)}
                          >
                            {color}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {selectedAIProductType && (
                  <div className="selected-variant-info">
                    <div className="variant-details">
                      <h6>
                        <i className="fa-solid fa-check-circle me-2"></i>
                        Producto Seleccionado
                      </h6>
                      <div className="variant-specs">
                        <span className="spec-item">
                          <strong>Tipo:</strong> {getAIProductTypes().find(t => t.id === selectedAIProductType)?.name}
                        </span>
                        {selectedAIProductType === 'remera' && selectedAISize && (
                          <span className="spec-item">
                            <strong>Talla:</strong> {selectedAISize}
                          </span>
                        )}
                        {selectedAIProductType === 'remera' && selectedAIColor && (
                          <span className="spec-item">
                            <strong>Color:</strong> {selectedAIColor}
                          </span>
                        )}
                        <span className="spec-item">
                          <strong>Precio:</strong> ${getAIProductTypes().find(t => t.id === selectedAIProductType)?.price.toLocaleString('es-AR')}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Variant Selection */}
            {!isAIProduct() && product.hasVariants && product.variants?.length > 0 ? (
              <div className="variant-selection">
                {/* Size Selection */}
                {getAvailableSizes().length > 0 && (
                  <div className="variant-group">
                    <div className="variant-label-row">
                      <label className="variant-label">
                        <i className="fa-solid fa-ruler me-2"></i>
                        Talla
                      </label>
                      {getSizeGuideType() && (
                        <button 
                          type="button"
                          className="size-guide-link"
                          onClick={() => setShowSizeGuide(true)}
                        >
                          <i className="fa-solid fa-ruler-combined me-1"></i>
                          Ver guía de talles
                        </button>
                      )}
                    </div>
                    <div className="variant-options">
                      {getAvailableSizes().map((size) => (
                        <button
                          key={size}
                          className={`variant-btn size-btn ${selectedSize === size ? 'selected' : ''}`}
                          onClick={() => handleSizeChange(size)}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Color Selection — solo si hay más de un color */}
                {hasMultipleColors() && (
                  <div className="variant-group">
                    <label className="variant-label">
                      <i className="fa-solid fa-palette me-2"></i>
                      Color
                    </label>
                    <div className="variant-options">
                      {getAvailableColors().map((color) => (
                        <button
                          key={color}
                          className={`variant-btn color-btn ${selectedColor === color ? 'selected' : ''}`}
                          onClick={() => handleColorChange(color)}
                        >
                          {color}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected Variant Info */}
                {selectedVariant && (
                  <div className="selected-variant-info">
                    <div className="variant-details">
                      <h6>
                        <i className="fa-solid fa-check-circle me-2"></i>
                        Variante Seleccionada
                      </h6>
                      <div className="variant-specs">
                        <span className="spec-item">
                          <strong>Talla:</strong> {selectedVariant.size}
                        </span>
                        {hasMultipleColors() && (
                          <span className="spec-item">
                            <strong>Color:</strong> {selectedVariant.color}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="legacy-selection">
                {/* Legacy Size Selection */}
                {product.size && product.size.length > 0 && (
                  <div className="legacy-group">
                    <label className="legacy-label">
                      <i className="fa-solid fa-ruler me-2"></i>
                      Talles Disponibles
                    </label>
                    <select
                      multiple
                      className="legacy-select"
                      value={selectedSizes}
                      onChange={handleSizeSelectChange}
                    >
                      {product.size.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Legacy Color Selection */}
                {product.color && product.color.length > 0 && (
                  <div className="legacy-group">
                    <label className="legacy-label">
                      <i className="fa-solid fa-palette me-2"></i>
                      Colores Disponibles
                    </label>
                    <select
                      multiple
                      className="legacy-select"
                      value={selectedColors}
                      onChange={handleColorSelectChange}
                    >
                      {product.color.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Quantity and Add to Cart */}
            <div className="purchase-section">
              <div className="quantity-section">
                <label className="quantity-label">
                  <i className="fa-solid fa-hashtag me-2"></i>
                  Cantidad
                </label>
                <div className="quantity-controls-single">
                  <button 
                    className="quantity-btn-single minus"
                    onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                    disabled={quantity <= 1}
                  >
                    <i className="fa-solid fa-minus"></i>
                  </button>
                  <span className="quantity-display-single">{quantity}</span>
                  <button 
                    className="quantity-btn-single plus"
                    onClick={() => setQuantity(prev => prev + 1)}
                  >
                    <i className="fa-solid fa-plus"></i>
                  </button>
                </div>
              </div>

              <div className="product-action-buttons">
                {/* Solo mostrar Agregar al Carrito si NO es personalizable */}
                {!isCustomizable() && (
                  <button 
                    onClick={handleAddToCart} 
                    className="add-to-cart-btn"
                    disabled={
                      (isAIProduct() && !selectedAIProductType) ||
                      (isAIProduct() && selectedAIProductType === 'remera' && (!selectedAISize || !selectedAIColor)) ||
                      (!isAIProduct() && product.hasVariants && (!selectedSize || (hasMultipleColors() && !selectedColor)))
                    }
                  >
                    <i className="fa-solid fa-cart-plus me-2"></i>
                    AGREGAR AL CARRITO
                  </button>
                )}
                
                {/* Virtual Try-On Button - No mostrar para productos personalizados */}
                {isTryOnEligible() && !isCustomizable() && (
                  <button 
                    onClick={() => setShowTryOnModal(true)}
                    className="try-on-btn"
                  >
                  <i class="fa-solid fa-wand-magic-sparkles me-2 "> </i> PROBARME
                  </button>
                )}

                {/* Customization Button for Merchandising */}
                {isCustomizable() && (
                  <button 
                    onClick={() => setShowCustomOrderForm(true)}
                    className="customize-btn"
                  >
                    <i className="fa-solid fa-comments me-2"></i>
                    SOLICITAR COTIZACIÓN
                  </button>
                )}
              </div>

              {/* Premium Service Notice for Customizable Products */}
              {isCustomizable() && (
                <div className="premium-service-notice">
                  <div className="notice-icon">
                    <i className="fa-solid fa-gem"></i>
                  </div>
                  <div className="notice-content">
                    <span className="notice-title">Servicio Premium Personalizado</span>
                    <span className="notice-text">
                      Los productos personalizados requieren confirmación manual. 
                      Envía tu diseño y te contactaremos para confirmar disponibilidad y precio final según cantidad.
                    </span>
                  </div>
                </div>
              )}

              {/* AI Generator Link for Customizable Products */}
              {isCustomizable() && (
                <div className="ai-generator-banner">
                  <div className="ai-banner-content">
                    <i className="fa-solid fa-wand-magic-sparkles"></i>
                    <div className="ai-banner-text">
                      <span className="ai-banner-title">¿No tienes una imagen?</span>
                      <span className="ai-banner-subtitle">Crea diseños únicos con Inteligencia Artificial</span>
                    </div>
                    <a 
                      href="https://ai.rementerprises.uy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="ai-banner-btn"
                    >
                      Crear con AI
                      <i className="fa-solid fa-arrow-right ms-2"></i>
                    </a>
                  </div>
                </div>
              )}
              
              {/* Help Message - Para productos AI */}
              {isAIProduct() && !selectedAIProductType && (
                <div className="selection-help">
                  <i className="fa-solid fa-info-circle me-2"></i>
                  Selecciona el tipo de producto para continuar
                </div>
              )}
              
              {/* Help Message - Para remeras AI sin talla/color */}
              {isAIProduct() && selectedAIProductType === 'remera' && (!selectedAISize || !selectedAIColor) && (
                <div className="selection-help">
                  <i className="fa-solid fa-info-circle me-2"></i>
                  Selecciona talla y color para continuar
                </div>
              )}
              
              {/* Help Message - Solo para productos NO personalizables con variantes */}
              {!isAIProduct() && !isCustomizable() && product.hasVariants && (!selectedSize || (hasMultipleColors() && !selectedColor)) && (
                <div className="selection-help">
                  <i className="fa-solid fa-info-circle me-2"></i>
                  {hasMultipleColors() ? 'Selecciona talla y color para continuar' : 'Selecciona un tamaño para continuar'}
                </div>
              )}
            </div>

            {/* Additional Info */}
            {(product.material || product.brand) && (
              <div className="additional-info">
                <h6 className="info-title">
                  <i className="fa-solid fa-info-circle me-2"></i>
                  Información Adicional
                </h6>
                <div className="info-grid">
                  {product.material && (
                    <div className="info-item">
                      <span className="info-label">Material:</span>
                      <span className="info-value">{product.material}</span>
                    </div>
                  )}
                  {product.brand && (
                    <div className="info-item">
                      <span className="info-label">Marca:</span>
                      <span className="info-value">{product.brand}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Virtual Try-On Modal */}
      <TryOnModal
        isOpen={showTryOnModal}
        onClose={() => setShowTryOnModal(false)}
        productImage={currentImage}
        productName={product.name}
      />

      {/* Custom Order Form Modal */}
      {showCustomOrderForm && (
        <div className="custom-order-modal-overlay" onClick={() => setShowCustomOrderForm(false)}>
          <div className="custom-order-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="custom-order-close-btn"
              onClick={() => setShowCustomOrderForm(false)}
            >
              <i className="fa-solid fa-times"></i>
            </button>
            <CustomOrderForm 
              product={product}
              selectedVariant={selectedVariant}
              onClose={() => setShowCustomOrderForm(false)}
              initialQuantity={quantity}
            />
          </div>
        </div>
      )}

      {/* Size Guide Modal */}
      <SizeGuideModal 
        isOpen={showSizeGuide}
        onClose={() => setShowSizeGuide(false)}
        productType={getSizeGuideType()}
      />
    </div>
    </>
  );
};

export default SingleProduct;
